/**
 * Cinematic replay: the "director" that turns a recorded ride into a short edit (src/app/cinematic.ts plays and
 * records it). Pure logic, no DOM or three.js: the ride trace in, a timeline of shots and slow-motion out.
 *
 * Rules (owner feedback 26): cut on action, 2-5 s per shot; a big air gets a low trackside camera at the nearest
 * jump (pre-placed from the level's jump / berm table along the track line) and slow motion around its apex; the
 * rest cycles chase, drone, orbit and one short helmet-cam segment.
 */

/** One sample of the ride, taken while it was ridden (src/app/session.ts, 30 per second). */
export interface TraceSample { t: number; x: number; y: number; z: number; speed: number; air: boolean; heading: number }

export type ShotKind = 'chase' | 'trackside' | 'drone' | 'orbit' | 'helmet';

export interface Shot {
  kind: ShotKind;
  /** Ride time (s) the shot starts and ends at. */
  from: number;
  to: number;
  /** Trackside: where the camera stands (ground height is added by the player). */
  anchor?: { x: number; z: number };
  /** Orbit / drone: which way round, +1 or -1. */
  dir?: number;
}

export interface SlowMo { from: number; to: number; scale: number }

export interface CinePlan { start: number; end: number; shots: Shot[]; slow: SlowMo[] }

/** A camera spot beside the track: jumps and berms of the level, a few metres off the line. */
export interface CamAnchor { x: number; z: number; kind: 'jump' | 'berm' }

export interface Air { takeoff: number; landing: number; apex: number; height: number }

export type CineLength = 'short' | 'long' | 'full';
export const LENGTH_SECONDS: Record<CineLength, number> = { short: 15, long: 30, full: Infinity };

/** The time scale of the big-air slow motion (owner: 0.35-0.5 around the apex). */
export const SLOW_SCALE = 0.4;
const BIG_AIR = 0.45; // s in the air for slow motion and a trackside shot
const MIN_SHOT = 2, MAX_SHOT = 5;

/** Airborne stretches of the trace (a single-sample bounce does not count). */
export function airs(trace: readonly TraceSample[]): Air[] {
  const out: Air[] = [];
  let i = 0;
  while (i < trace.length) {
    if (!trace[i]!.air) { i++; continue; }
    let j = i, top = i;
    while (j + 1 < trace.length && trace[j + 1]!.air) { j++; if (trace[j]!.y > trace[top]!.y) top = j; }
    const takeoff = trace[i]!.t, landing = trace[j]!.t;
    const base = Math.min(trace[i]!.y, trace[j]!.y);
    if (landing - takeoff >= 0.1) out.push({ takeoff, landing, apex: trace[top]!.t, height: trace[top]!.y - base });
    i = j + 1;
  }
  return out;
}

/** Ride time -> video time: slow-motion stretches last 1/scale longer. */
export function videoSeconds(from: number, to: number, slow: readonly SlowMo[]): number {
  let v = to - from;
  for (const s of slow) {
    const a = Math.max(from, s.from), b = Math.min(to, s.to);
    if (b > a) v += (b - a) * (1 / s.scale - 1);
  }
  return v;
}

/** The time scale at ride time t (1 outside slow motion), eased in and out over 0.25 s so the change is not a jolt. */
export function timeScaleAt(t: number, slow: readonly SlowMo[]): number {
  for (const s of slow) {
    if (t < s.from || t > s.to) continue;
    const ramp = Math.min(1, (t - s.from) / 0.25, (s.to - t) / 0.25);
    return 1 + (s.scale - 1) * Math.max(0, ramp);
  }
  return 1;
}

function slowFor(a: Air): SlowMo {
  // Centred on the apex, covering most of the flight.
  const half = Math.min(0.9, (a.landing - a.takeoff) * 0.5 + 0.1);
  return { from: Math.max(a.takeoff - 0.1, a.apex - half), to: Math.min(a.landing + 0.15, a.apex + half), scale: SLOW_SCALE };
}

/** How good a stretch of the ride is to watch: air time counts most, then speed. */
function score(trace: readonly TraceSample[], from: number, to: number, big: readonly Air[]): number {
  let s = 0;
  for (const a of big) if (a.takeoff >= from && a.landing <= to) s += 10 + (a.landing - a.takeoff) * 10 + a.height * 3;
  let n = 0, v = 0;
  for (const p of trace) if (p.t >= from && p.t <= to) { n++; v += p.speed; }
  return s + (n ? v / n : 0) * 0.3;
}

/**
 * Picks the stretch of the ride to show: the best window whose video (slow motion included) lasts about `seconds`,
 * or the whole ride. Windows start at most 1.5 s before a takeoff is cut off at their start.
 */
export function pickWindow(trace: readonly TraceSample[], seconds: number, focus?: number): { start: number; end: number } {
  const first = trace[0]?.t ?? 0, last = trace[trace.length - 1]?.t ?? 0;
  if (!Number.isFinite(seconds) || last - first <= seconds * 0.6) return { start: first, end: last };
  const big = airs(trace).filter((a) => a.landing - a.takeoff >= BIG_AIR);
  const slow = big.map(slowFor);
  if (focus !== undefined && focus >= first && focus <= last) return centredWindow(first, last, seconds, focus, big, slow);
  let best = { start: first, end: Math.min(last, first + seconds), score: -1 };
  for (let s = first; s < last; s += 0.5) {
    // Grow the window until its video is long enough.
    let e = s + seconds * 0.5;
    while (e < last && videoSeconds(s, e, slow) < seconds) e += 0.1;
    if (e > last) break;
    // Never start in the middle of a flight.
    if (big.some((a) => s > a.takeoff - 1 && s < a.landing)) continue;
    const sc = score(trace, s, e, big);
    if (sc > best.score) best = { start: s, end: e, score: sc };
  }
  return { start: best.start, end: best.end };
}

/**
 * The window around a highlight (the biggest of the ride, src/game/highlights.ts): it sits a little past the middle
 * of the video, the window never starts mid-flight, and it slides back when the ride ends too soon after it.
 */
function centredWindow(first: number, last: number, seconds: number, focus: number, big: readonly Air[], slow: readonly SlowMo[]): { start: number; end: number } {
  const span = (s: number) => { let e = s + seconds * 0.5; while (e < last && videoSeconds(s, e, slow) < seconds) e += 0.1; return Math.min(e, last); };
  let s = Math.max(first, focus - seconds * 0.45);
  const flight = big.find((a) => s > a.takeoff - 1 && s < a.landing);
  if (flight) s = Math.max(first, flight.takeoff - 1.1);
  let e = span(s);
  // Too close to the end: slide back until the video is long enough (the focus stays inside).
  while (e >= last && s > first && videoSeconds(s, e, slow) < seconds) { s = Math.max(first, s - 0.1); e = span(s); }
  return { start: s, end: e };
}

const near = (anchors: readonly CamAnchor[], x: number, z: number, max: number): CamAnchor | null => {
  let best: CamAnchor | null = null, d = max * max;
  for (const a of anchors) {
    const q = (a.x - x) ** 2 + (a.z - z) ** 2;
    if (q < d) { d = q; best = a; }
  }
  return best;
};

const sampleAt = (trace: readonly TraceSample[], t: number): TraceSample => {
  let lo = 0, hi = trace.length - 1;
  while (lo < hi) { const m = (lo + hi) >> 1; if (trace[m]!.t < t) lo = m + 1; else hi = m; }
  return trace[lo]!;
};

/** The order filler shots come in between the jumps (one helmet segment per video). */
const FILLER: ShotKind[] = ['chase', 'drone', 'chase', 'helmet', 'orbit', 'chase', 'drone', 'orbit'];

/**
 * The edit for a ride: window, shots (contiguous, 2-5 s of video each, cut on takeoff and landing) and slow motion.
 * `anchors` are the level's trackside camera spots; a big air with none within 45 m gets one beside its apex.
 * `focus`: the ride time of the biggest highlight; the window is centred on it instead of scored.
 */
export function planCinematic(trace: readonly TraceSample[], anchors: readonly CamAnchor[], length: CineLength, focus?: number): CinePlan {
  const { start, end } = pickWindow(trace, LENGTH_SECONDS[length], focus);
  const big = airs(trace).filter((a) => a.landing - a.takeoff >= BIG_AIR && a.takeoff > start + 0.5 && a.landing < end - 0.3);
  const slow = big.map(slowFor);
  const shots: Shot[] = [];
  let filler = 0, helmetUsed = false, dir = 1;
  const nextKind = (): ShotKind => {
    for (;;) {
      const k = FILLER[filler++ % FILLER.length]!;
      if (k === 'helmet') { if (helmetUsed) continue; helmetUsed = true; }
      return k;
    }
  };
  // Fill [a, b] with filler shots of 2-5 s of video (b - a is ride time; no slow motion between jumps).
  const fill = (a: number, b: number) => {
    let t = a;
    while (b - t > 1e-6) {
      const left = b - t;
      let len = Math.min(MAX_SHOT, left);
      if (left > MAX_SHOT && left - MAX_SHOT < MIN_SHOT) len = left / 2; // never leave a stub
      if (left < MIN_SHOT && shots.length) { shots[shots.length - 1]!.to = b; return; }
      const k = nextKind();
      shots.push({ kind: k, from: t, to: t + (k === 'helmet' ? Math.min(len, 3) : len), dir: (dir = -dir) });
      t = shots[shots.length - 1]!.to;
    }
  };
  let cursor = start;
  for (const a of big) {
    const cutIn = a.takeoff - 1.1;   // the trackside shot opens just before the face
    if (cutIn < cursor + 0.5) continue; // too close after the last jump shot: it rides on in that one
    fill(cursor, cutIn);
    const at = sampleAt(trace, a.apex);
    const anchor = near(anchors, at.x, at.z, 45) ?? sideOf(at, 8);
    shots.push({ kind: 'trackside', from: cutIn, to: a.landing + 0.7, anchor: { x: anchor.x, z: anchor.z } });
    cursor = a.landing + 0.7; // cut on the landing
  }
  fill(cursor, end);
  return { start, end, shots, slow };
}

/** A spot `d` m to the side of a sample's heading (heading = atan2(x, z) of the bike's forward). */
export function sideOf(p: TraceSample, d: number): { x: number; z: number } {
  return { x: p.x + Math.cos(p.heading) * d, z: p.z - Math.sin(p.heading) * d };
}

/** The shot playing at ride time t. */
export function shotAt(plan: CinePlan, t: number): Shot {
  for (const s of plan.shots) if (t < s.to) return s;
  return plan.shots[plan.shots.length - 1] ?? { kind: 'chase', from: plan.start, to: plan.end };
}

// ---------- recording and sharing ----------

/** Container / codec preference: MP4 (H.264) where the browser records it (Chrome, Edge, Safari), else WebM VP9. */
export const MIME_PREFERENCE = [
  'video/mp4;codecs=avc1.640028,mp4a.40.2',
  'video/mp4;codecs=avc1,mp4a',
  'video/mp4;codecs=avc1',
  'video/mp4',
  'video/webm;codecs=vp9,opus',
  'video/webm;codecs=vp8,opus',
  'video/webm',
] as const;

export function pickMime(supported: (m: string) => boolean): string | null {
  return MIME_PREFERENCE.find((m) => supported(m)) ?? null;
}

export type CineFormat = 'landscape' | 'vertical';
export const FORMAT_SIZE: Record<CineFormat, { w: number; h: number }> = { landscape: { w: 1920, h: 1080 }, vertical: { w: 1080, h: 1920 } };

export function videoFileName(track: string, mime: string, date = new Date()): string {
  const slug = track.toLowerCase().normalize('NFKD').replace(/[^\w]+/g, '-').replace(/^-|-$/g, '') || 'ride';
  const d = date.toISOString().slice(0, 10);
  return `megamotocross-${slug}-${d}.${mime.startsWith('video/mp4') ? 'mp4' : 'webm'}`;
}

export const HASHTAGS = '#megamotocross #motocross #MXZielonka';
export const SHARE_URL = 'https://megamotocross.pl';

/** The stat line of the caption: "1:12.4 at MX Zielonka, 2.1 s air" (the parts already localized), else `fallback`. */
export function statLine(parts: readonly (string | null)[], fallback: string): string {
  const p = parts.filter((x): x is string => !!x);
  return p.length ? p.join(', ') : fallback;
}

/** The caption posted with the video: the localized line, the time if there is one, the hashtags and the link. */
export function caption(line: string, time: string | null): string {
  return [time ? `${line} ${time}` : line, HASHTAGS, SHARE_URL].join('\n');
}
